import type { CryptoSwapsTranslations } from "@/shared/lang/interfaces/cryptoSwaps";
import useTranslations from "@/shared/lang/useTranslations";

import type { TicketNotice } from "../components/swap/QuoteTicket";
import { CONCEPT_MAX_LENGTH, SAVED_WALLET_ALIAS_MAX_LENGTH } from "../constants/cryptoSwaps.constants";
import { SELL_RANGE_KEYS } from "../utils/cryptoSwapAmountIssues";
import { cryptoSwapErrorText, isRangeRejection } from "../utils/cryptoSwapErrors";
import { CRYPTO_SWAP_BLOCK_MESSAGE_KEYS, type CryptoSwapBlock } from "../utils/cryptoSwapStatus";
import { useAmountIssueText } from "./useAmountIssueText";
import type { UseCryptoSellFormResult } from "./useCryptoSellForm";
import { useCryptoSwapLabels } from "./useCryptoSwapLabels";

/** The sale's own wording where the purchase's would say "buy"; the rest is shared. */
const SELL_BLOCK_MESSAGE_KEYS: Partial<Record<NonNullable<CryptoSwapBlock>, keyof CryptoSwapsTranslations>> =
  {
    readonly: "sell_block_readonly",
    unavailable: "sell_block_unavailable",
    empty: "sell_block_empty",
    destination: "sell_block_refund",
  };

/** The review button says exactly what the offending field says; the ticket explains what a field cannot. */
export function useCryptoSellFormMessages(form: UseCryptoSellFormResult) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const issueText = useAmountIssueText(form.pair, SELL_RANGE_KEYS);

  const quoteError = form.quoteError;
  const quoteErrorText = quoteError ? cryptoSwapErrorText(quoteError, t) : null;
  const network = labels.networkShort(form.network);

  const refundText =
    form.refundIssue === "empty"
      ? t("sell_refund_address_empty_hint")
      : form.refundIssue === "invalid"
        ? t("destination_address_invalid", { network })
        : form.refundIssue === "unsupported_network"
          ? t("destination_network_unsupported")
          : form.refundIssue === "concept"
            ? t("concept_too_long", { max: CONCEPT_MAX_LENGTH })
            : form.refundIssue === "save_alias"
              ? t("saved_wallet_alias_too_long", { max: SAVED_WALLET_ALIAS_MAX_LENGTH })
              : null;

  const blockMessage = form.reviewIssue
    ? issueText(form.reviewIssue)
    : form.block === "destination" && refundText
      ? refundText
      : form.block
        ? t(SELL_BLOCK_MESSAGE_KEYS[form.block] ?? CRYPTO_SWAP_BLOCK_MESSAGE_KEYS[form.block])
        : null;

  const notice: TicketNotice | null =
    form.block === "expired"
      ? { kind: "expired" }
      : form.isPricing
        ? null
        : form.isManualMode
          ? { kind: "manual" }
          : quoteError && quoteErrorText && !isRangeRejection(quoteError) && !form.quote
            ? { kind: "failed", message: quoteErrorText, retryable: quoteError.retryable }
            : null;

  return { blockMessage, notice, quoteErrorText };
}
